import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Clock } from 'lucide-react';
import { useLeaveMutations } from '@/hooks/leave/useLeaveMutations';
import type { LeaveRequest, UpdateLeaveRequestInput } from '@/services/leave/types';
import { useMyAssignedLeaveTypes } from '@/hooks/leave/useMyAssignedLeaveTypes';

const MAX_PART_DAY_HOURS = 7.5;

// Helper to calculate hours from time strings
const calculateHoursFromTimes = (start: string, end: string): number => {
  if (!start || !end) return 0;
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  return Math.max(0, (endMinutes - startMinutes) / 60);
};

interface EditLeaveRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leaveRequest: LeaveRequest;
}

export const EditLeaveRequestDialog = ({ open, onOpenChange, leaveRequest }: EditLeaveRequestDialogProps) => {
  const { updateMyLeaveRequest, editApprovedLeaveRequest } = useLeaveMutations();
  const { assignedLeaveTypes, isLoading: isLoadingLeaveTypes } = useMyAssignedLeaveTypes();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isApprovedRequest = leaveRequest.status === 'approved';

  // Detect if existing request is part-day
  const isExistingPartDay = leaveRequest.requested_hours !== null && 
                            leaveRequest.requested_hours !== undefined &&
                            leaveRequest.requested_hours > 0;

  const [formData, setFormData] = useState<UpdateLeaveRequestInput>({
    leave_type: leaveRequest.leave_type,
    start_date: leaveRequest.start_date,
    end_date: leaveRequest.end_date,
    reason: leaveRequest.reason || '',
    is_part_day: isExistingPartDay,
    requested_hours: leaveRequest.requested_hours || undefined,
    start_time: leaveRequest.start_time?.substring(0, 5) || undefined,
    end_time: leaveRequest.end_time?.substring(0, 5) || undefined,
  });

  const [isPartDay, setIsPartDay] = useState(isExistingPartDay);
  const [startTime, setStartTime] = useState(leaveRequest.start_time?.substring(0, 5) || '');
  const [endTime, setEndTime] = useState(leaveRequest.end_time?.substring(0, 5) || '');

  // Calculate hours from times
  const calculatedHours = isPartDay && startTime && endTime 
    ? calculateHoursFromTimes(startTime, endTime) 
    : (formData.requested_hours || 0);

  // Reset form data when dialog opens with a new request
  useEffect(() => {
    if (open) {
      const existingPartDay = leaveRequest.requested_hours !== null && 
                              leaveRequest.requested_hours !== undefined &&
                              leaveRequest.requested_hours > 0;
      
      console.log('[EditLeaveRequestDialog] Dialog opened for request:', {
        id: leaveRequest.id,
        status: leaveRequest.status,
        isApproved: isApprovedRequest,
        isPartDay: existingPartDay,
        requested_hours: leaveRequest.requested_hours,
        start_time: leaveRequest.start_time,
        end_time: leaveRequest.end_time
      });
      
      setFormData({
        leave_type: leaveRequest.leave_type,
        start_date: leaveRequest.start_date,
        end_date: leaveRequest.end_date,
        reason: leaveRequest.reason || '',
        is_part_day: existingPartDay,
        requested_hours: leaveRequest.requested_hours || undefined,
        start_time: leaveRequest.start_time?.substring(0, 5) || undefined,
        end_time: leaveRequest.end_time?.substring(0, 5) || undefined,
      });
      setIsPartDay(existingPartDay);
      setStartTime(leaveRequest.start_time?.substring(0, 5) || '');
      setEndTime(leaveRequest.end_time?.substring(0, 5) || '');
    }
  }, [open, leaveRequest]);

  const handlePartDayChange = (checked: boolean) => {
    setIsPartDay(checked);
    if (checked && formData.start_date) {
      setFormData(prev => ({ ...prev, end_date: prev.start_date, is_part_day: true }));
    } else {
      setFormData(prev => ({ ...prev, is_part_day: false, requested_hours: undefined, start_time: undefined, end_time: undefined }));
      setStartTime('');
      setEndTime('');
    }
  };

  const handleStartDateChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      start_date: value,
      end_date: isPartDay ? value : prev.end_date,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) {
      console.log('[EditLeaveRequestDialog] Preventing double submission');
      return;
    }

    // Part-day validation
    if (isPartDay) {
      if (!startTime || !endTime) {
        console.error('[EditLeaveRequestDialog] Start and end times required for part-day');
        return;
      }
      const hours = calculateHoursFromTimes(startTime, endTime);
      if (hours <= 0) {
        console.error('[EditLeaveRequestDialog] End time must be after start time');
        return;
      }
      if (hours > MAX_PART_DAY_HOURS) {
        console.error('[EditLeaveRequestDialog] Hours exceed max for part-day');
        return;
      }
      if (!formData.reason || formData.reason.trim().length === 0) {
        console.error('[EditLeaveRequestDialog] Reason required for part-day');
        return;
      }
    }
    
    setIsSubmitting(true);

    const submitData: UpdateLeaveRequestInput = {
      ...formData,
      end_date: isPartDay ? formData.start_date : formData.end_date,
      is_part_day: isPartDay,
      requested_hours: isPartDay ? calculatedHours : undefined,
      start_time: isPartDay ? startTime : undefined,
      end_time: isPartDay ? endTime : undefined,
    };

    console.log('[EditLeaveRequestDialog] handleSubmit - Starting:', {
      requestId: leaveRequest.id,
      originalStatus: leaveRequest.status,
      isApprovedRequest,
      isPartDay,
      calculatedHours,
      startTime,
      endTime,
      submitData
    });

    try {
      if (isApprovedRequest) {
        console.log('[EditLeaveRequestDialog] Calling editApprovedLeaveRequest');
        await editApprovedLeaveRequest.mutateAsync({
          requestId: leaveRequest.id,
          request: submitData,
        });
      } else {
        console.log('[EditLeaveRequestDialog] Calling updateMyLeaveRequest');
        await updateMyLeaveRequest.mutateAsync({
          requestId: leaveRequest.id,
          request: submitData,
        });
      }
      console.log('[EditLeaveRequestDialog] Update successful');
      onOpenChange(false);
    } catch (error) {
      console.error('[EditLeaveRequestDialog] Failed to update leave request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof UpdateLeaveRequestInput, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid = isPartDay 
    ? (startTime && endTime && calculatedHours > 0 && calculatedHours <= MAX_PART_DAY_HOURS && formData.reason)
    : true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isApprovedRequest ? 'Edit Approved Leave' : 'Edit Leave Request'}
          </DialogTitle>
        </DialogHeader>
        
        {isApprovedRequest && (
          <Alert variant="default" className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <strong>Important:</strong> Editing this approved leave will change its status back to <strong>pending</strong> and require re-approval. Your balance will be adjusted accordingly.
            </AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="leave_type">Leave Type</Label>
            <Select
              value={formData.leave_type}
              onValueChange={(value) => handleInputChange('leave_type', value)}
              disabled={isLoadingLeaveTypes}
            >
              <SelectTrigger>
                <SelectValue placeholder={isLoadingLeaveTypes ? "Loading..." : "Select leave type"} />
              </SelectTrigger>
              <SelectContent>
                {assignedLeaveTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Part Day Checkbox */}
          <div className="flex items-center space-x-2 p-3 border rounded-lg bg-muted/30">
            <Checkbox
              id="edit-part-day"
              checked={isPartDay}
              onCheckedChange={handlePartDayChange}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="edit-part-day"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                Part Day Request
              </Label>
              <p className="text-xs text-muted-foreground">
                Request a partial day (specific hours) instead of full days
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">{isPartDay ? 'Date' : 'Start Date'}</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => handleStartDateChange(e.target.value)}
                required
              />
            </div>
            {!isPartDay && (
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  required
                />
              </div>
            )}
          </div>

          {/* Time Pickers for Part Day */}
          {isPartDay && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_start_time">Start Time *</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="edit_start_time"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="pl-9"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_end_time">End Time *</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="edit_end_time"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="pl-9"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Calculated Hours Display for Part Day */}
          {isPartDay && startTime && endTime && (
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Calculated hours: <strong>{calculatedHours.toFixed(1)}h</strong>
                {calculatedHours > MAX_PART_DAY_HOURS && (
                  <span className="text-destructive ml-2">
                    (exceeds {MAX_PART_DAY_HOURS}h limit)
                  </span>
                )}
                {calculatedHours <= 0 && (
                  <span className="text-destructive ml-2">
                    (invalid time range)
                  </span>
                )}
              </span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason">
              {isPartDay ? 'Description of Part Day *' : 'Reason (Optional)'}
            </Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              placeholder={isPartDay 
                ? "Describe which part of the day and why..." 
                : "Enter reason for leave..."
              }
              rows={3}
              required={isPartDay}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || !isFormValid}
            >
              {isSubmitting 
                ? 'Updating...' 
                : isApprovedRequest 
                  ? 'Update & Submit for Re-approval' 
                  : 'Update Request'
              }
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
